import * as z from 'zod'

// Shared link-target shape for the Navigation and Footer settings forms —
// mirrors the backend's `linkTargetInputFields`/`LinkTargetArgs`
// (`furnisystems-backend/src/types-modified/LinkTarget.ts`): a `link_type`
// discriminator plus one FK per branch, with `external_url` as the escape
// hatch when no internal target type is chosen. Both containers (nav items,
// featured cards, footer links) embed this same 6-field shape at every
// layer — type, zod schema, blank/seed factory, mutation-variable builder,
// and "needs a target" validation — so it lives here once instead of being
// copy-pasted per container.

export type CtaLinkType = 'cms_page' | 'category' | 'store' | 'collection' | null

export type LinkTargetFormValues = {
  link_type: CtaLinkType
  link_page_id: string
  link_category_id: number | null
  link_collection_id: number | null
  external_url: string
  link_new_tab: boolean
}

export const ctaLinkTypeEnum = z.enum(['cms_page', 'category', 'store', 'collection']).nullable()

/**
 * Zod fragment for the 6 fields — spread into whichever object schema owns
 * them, alongside that schema's own fields, so every schema that embeds a
 * link target also inherits the "no spaces in the URL" guard below for free.
 */
export const linkTargetSchemaFields = {
  link_type: ctaLinkTypeEnum,
  link_page_id: z.string(),
  link_category_id: z.number().nullable(),
  link_collection_id: z.number().nullable(),
  // Trimmed before checking so a trailing/leading space picked up from a
  // paste doesn't block saving — `buildLinkTargetVariables` trims the same
  // way on write. Internal spaces (a genuinely malformed URL) still fail.
  external_url: z.string().refine(v => !/\s/.test(v.trim()), {
    message: 'URL cannot contain spaces',
  }),
  link_new_tab: z.boolean(),
}

export function blankLinkTarget(): LinkTargetFormValues {
  return {
    link_type: null,
    link_page_id: '',
    link_category_id: null,
    link_collection_id: null,
    external_url: '',
    link_new_tab: false,
  }
}

export function mapLinkTargetFromRaw(raw: any): LinkTargetFormValues {
  return {
    link_type: raw?.link_type ?? null,
    link_page_id: raw?.link_page_id ?? '',
    link_category_id: raw?.link_category_id ?? null,
    link_collection_id: raw?.link_collection_id ?? null,
    external_url: raw?.external_url ?? '',
    link_new_tab: raw?.link_new_tab ?? false,
  }
}

/**
 * A bare domain (e.g. `vilmers.com/promo`, no scheme) reaches the portal
 * indistinguishable from a site-relative path and gets rendered as one
 * (`/envilmers.com/promo`). Normalize on save: trim, and for a non-empty
 * value that isn't already an absolute `http(s)` URL or an explicit
 * site-relative path (leading `/`), prepend `https://`. Deliberately no
 * fuller URL parsing/validation beyond that.
 */
function normalizeExternalUrl(value: string): string {
  const trimmed = value.trim()
  if (!trimmed) return trimmed
  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('/')) return trimmed
  return `https://${trimmed}`
}

/**
 * Build the mutation-variable shape for one link target from validated form
 * values. PURE — no network calls. Mirrors the backend's "omitted target
 * fields written as null" contract: every field that isn't the active
 * `link_type` branch is still sent explicitly (as `null`/`false`) so
 * switching target types clears the old one server-side.
 */
export function buildLinkTargetVariables(values: LinkTargetFormValues) {
  return {
    link_type: values.link_type,
    link_page_id: values.link_page_id || null,
    link_category_id: values.link_category_id,
    link_collection_id: values.link_collection_id,
    external_url: normalizeExternalUrl(values.external_url) || null,
    link_new_tab: values.link_new_tab,
  }
}

/**
 * Shared "needs an internal target or an external URL" `superRefine` check.
 * `path` is the base path to the object holding the 6 fields (e.g.
 * `['items', index]` or `['columns', columnIndex, 'links', linkIndex]`) —
 * the issue is attached to `[...path, 'external_url']`, the field every
 * caller renders a `CtaEditor`/`FormMessage` for.
 */
export function linkTargetRefine(
  values: LinkTargetFormValues,
  ctx: z.RefinementCtx,
  path: (string | number)[]
) {
  const hasInternalTarget = Boolean(values.link_type) || Boolean(values.link_page_id)
  const hasExternalUrl = Boolean(values.external_url?.trim())
  if (!hasInternalTarget && !hasExternalUrl) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Choose an internal target or enter an external URL',
      path: [...path, 'external_url'],
    })
  }
}
